/**
 * @fileoverview Utilidades READ-ONLY para acceso a tokens en interceptors
 *
 * IMPORTANTE: Este módulo solo LEE tokens de localStorage.
 * La ESCRITURA de tokens se hace exclusivamente a través de useAuthStore()
 * que usa useStorage de VueUse para mantener reactividad.
 *
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

/**
 * Claves de localStorage para tokens
 * Deben coincidir con las usadas en auth.store.ts
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  EXPIRATION_DATE: 'expirationDate',
} as const;

/**
 * Obtiene el token de acceso desde localStorage
 * @returns Token de acceso o null si no existe
 *
 * @example
 * const token = getAccessToken();
 * if (token) {
 *   // Usar token
 * }
 */
export const getAccessToken = (): string | null => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  return token && token.trim() !== '' ? token : null;
};

/**
 * Obtiene la fecha de expiración del token desde localStorage
 * @returns Timestamp de expiración en milisegundos o null si no existe/es inválido
 *
 * @example
 * const expiration = getExpirationDate();
 * if (expiration) {
 *   const isExpired = Date.now() > expiration;
 * }
 */
export const getExpirationDate = (): number | null => {
  const expirationStr = localStorage.getItem(STORAGE_KEYS.EXPIRATION_DATE);

  if (!expirationStr) {
    return null;
  }

  const timestamp = Number(expirationStr);

  if (Number.isNaN(timestamp) || timestamp <= 0) {
    console.warn('⚠️ Fecha de expiración inválida en localStorage:', expirationStr);
    return null;
  }

  return timestamp;
};

/**
 * Verifica si existe un token válido en localStorage
 * @returns true si existe un token no vacío, false en caso contrario
 *
 * @example
 * // En interceptors (no reactivo, solo lectura)
 * if (hasValidToken()) {
 *   // Proceder con petición autenticada
 * }
 */
export const hasValidToken = (): boolean => {
  const token = getAccessToken();
  return token !== null && token.trim() !== '';
};
